type CopyBinderOptions = {
    /** Attribute holding the text to copy */
    attr?: string; // default: "data-copy"
    /** Optional selector for a label inside the button to show feedback */
    labelSelector?: string; // default: "[data-copy-label]"
    /** Text shown after copy */
    successText?: string; // default: "Copiado!"
    /** Revert delay */
    resetMs?: number; // default: 2000
    /** Called on success */
    onSuccess?: (el: HTMLElement, copiedText: string) => void;
    /** Called on error */
    onError?: (el: HTMLElement, error: unknown) => void;
};

/**
 * Bind copy-to-clipboard to elements matching selector.
 * Uses event delegation (safe for dynamically injected DOM).
 */
export function bindCopyToClipboard(
    selector: string,
    options: CopyBinderOptions = {}
): () => void {
    const {
        attr = "data-copy",
        labelSelector = "[data-copy-label]",
        successText = "Copiado!",
        resetMs = 2000,
        onSuccess,
        onError,
    } = options;

    const handler = async (e: Event) => {
        const target = e.target as Element | null;
        if (!target) return;

        const el = target.closest(selector) as HTMLElement | null;
        if (!el) return;

        const text = el.getAttribute(attr) ?? "";
        if (!text) return;

        e.preventDefault();

        try {
            await writeClipboard(text);

            // Optional feedback swap
            const label = el.querySelector(labelSelector) as HTMLElement | null;
            if (label) {
                const original = label.textContent ?? "";
                label.textContent = successText;
                window.setTimeout(() => {
                    label.textContent = original;
                }, resetMs);
            }

            onSuccess?.(el, text);
        } catch (err) {
            onError?.(el, err);
        }
    };

    document.addEventListener("click", handler, { passive: false });

    // return unbind
    return () => document.removeEventListener("click", handler);
}

/** Clipboard write with fallback for older browsers */
async function writeClipboard(text: string): Promise<void> {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return;
    }

    // Fallback: execCommand (still useful on some older Safari/iframes)
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    ta.style.top = "0";
    document.body.appendChild(ta);
    ta.select();

    const ok = document.execCommand("copy");
    document.body.removeChild(ta);

    if (!ok) {
        throw new Error("Copy failed");
    }
}